import React, { useEffect, useMemo, useState } from "react";
import { X, ShieldCheck, RefreshCw, Plus, Minus, Truck, Check, Lock, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useBackClose } from "@/lib/backstack";
import WhatsAppIcon from "./WhatsAppIcon";
import SmartImage from "./SmartImage";
import { buildStockEnquiryUrl } from "@/lib/whatsapp";
import { buildProductWhatsAppUrl } from "@/lib/whatsapp";

const DTID = {
  modal: "product-modal",
  close: "product-modal-close",
  optionBtn: (group, value) =>
    `option-${group.toLowerCase().replace(/\s+/g, "-")}-${value.toLowerCase().replace(/\s+/g, "-")}`,
  addBtn: "product-modal-add-btn",
  qtyInc: "product-modal-qty-inc",
  qtyDec: "product-modal-qty-dec",
  whatsapp: "product-modal-whatsapp",
  price: "product-modal-price",
};

/**
 * Find the variant matching the current selection set.
 */
function findVariant(product, selections) {
  if (!product?.variants?.length) return null;
  return product.variants.find((v) =>
    Object.entries(selections).every(([k, val]) => v.selections[k] === val)
  );
}

function defaultSelections(product) {
  if (!product?.option_groups?.length || !product?.variants?.length) return {};
  const groups = Object.fromEntries(product.option_groups.map((g) => [g.name, g.values]));
  if (groups.Accessories && groups["Plate Model"]) {
    // Modular configurator defaults — accessory mode (plate deselected)
    const model = groups.Model?.[0];
    let colour = groups.Colour?.[0];
    if (model) {
      const ref = product.variants.find(
        (v) => v.selections.Model === model && v.selections.Colour && v.selections.Accessories
      );
      if (ref) colour = ref.selections.Colour;
    }
    return {
      ...(model ? { Model: model } : {}),
      ...(colour ? { Colour: colour } : {}),
      Accessories: groups.Accessories[0],
    };
  }
  // Default to selections of the cheapest variant.
  const cheapest = product.variants.reduce(
    (best, v) => (best === null || v.price < best.price ? v : best),
    null
  );
  const sel = { ...cheapest.selections };
  // Curve default follows group order (C first), not variant array order.
  if (groups.Curve && sel.Curve && sel.Curve !== groups.Curve[0]) {
    const candidate = { ...sel, Curve: groups.Curve[0] };
    if (findVariant(product, candidate)) return candidate;
  }
  return sel;
}

const SWITCH_MODULES = ["1-Way Switch", "2-Way Switch"];
const AMP_COMBINED_MODULES = ["1-Way Switch", "2-Way Switch", "Socket"];
const SOCKET_AMPS = ["6A", "16A"];
const MODE_HINTS = {
  plate: "Price = Plate + Colour",
  switch: "Price = Module + Amp + Colour",
  module: "Price = Module + Colour",
};
const BULK_INPUT_GROUPS = ["Amps", "Ways", "Flexy Model", "Enclosure Model", "W/P Box Model", "AC Box Model", "Color", "Accessories", "Plate Size"];
const SUBTYPE_GROUPS = ["DB Type", "AC Box Type"];

export default function ProductDetailDialog({ product, open, onClose }) {
  const { addItem } = useCart();
  const { user, openLogin } = useAuth();
  const [selections, setSelections] = useState({});
  const [mode, setMode] = useState("plate");
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [bulkQty, setBulkQty] = useState({});
  useBackClose(open && !!product, () => onClose?.());

  useEffect(() => {
    if (product) {
      setSelections(defaultSelections(product));
      setMode("plate");
      setQty(1);
      setAdded(false);
      setBulkQty({});
    }
  }, [product]);

  const groupsByName = useMemo(() => {
    const m = {};
    (product?.option_groups || []).forEach((g) => { m[g.name] = g.values; });
    return m;
  }, [product]);

  const hasPlates = !!groupsByName.Plate;
  const hasTypes = !!groupsByName.Type;
  const isMyrius = !!groupsByName.Accessories && !!groupsByName["Plate Model"];
  const isWireBulk = !!groupsByName.Gauge && !!groupsByName.Color;
  const isAmpBulk = !!groupsByName.Amps && product?.category_slug === "mcb-distribution";

  // For products with a "Type" group (e.g. MCB vs Isolator): which values of every
  // other group actually exist in variants of the currently selected Type.
  const typeAllowed = useMemo(() => {
    if (!hasTypes || !product?.variants?.length || !selections.Type) return null;
    const allowed = {};
    for (const v of product.variants) {
      if (v.selections.Type !== selections.Type) continue;
      for (const [k, val] of Object.entries(v.selections)) {
        (allowed[k] = allowed[k] || new Set()).add(val);
      }
    }
    return allowed;
  }, [hasTypes, product, selections.Type]);

  // Second-level filter: within a Type, which values co-exist with the selected
  // sub-type (DB Type / AC Box Type) — e.g. SPN has no 6 Way; Tempra has its own models.
  const subTypeGroup = SUBTYPE_GROUPS.find((g) => selections[g]);
  const dbAllowed = useMemo(() => {
    if (!hasTypes || !product?.variants?.length || !subTypeGroup) return null;
    const allowed = {};
    for (const v of product.variants) {
      if (v.selections.Type !== selections.Type) continue;
      if (v.selections[subTypeGroup] !== selections[subTypeGroup]) continue;
      for (const [k, val] of Object.entries(v.selections)) {
        (allowed[k] = allowed[k] || new Set()).add(val);
      }
    }
    return allowed;
  }, [hasTypes, product, selections.Type, subTypeGroup, selections[subTypeGroup]]);

  const anchoredSelections = useMemo(() => {
    if (!hasPlates) return selections;
    const a = { ...selections };
    if (mode === "plate") {
      if (groupsByName.Amp) a.Amp = groupsByName.Amp[0];
      if (groupsByName.Module) a.Module = groupsByName.Module.includes("Blank") ? "Blank" : groupsByName.Module[0];
    } else if (mode === "switch") {
      a.Plate = groupsByName.Plate[0];
      if (groupsByName.Module && !AMP_COMBINED_MODULES.includes(a.Module)) a.Module = SWITCH_MODULES[0];
      if (a.Module === "Socket" && !SOCKET_AMPS.includes(a.Amp)) a.Amp = "6A";
    } else if (mode === "module") {
      a.Plate = groupsByName.Plate[0];
      if (groupsByName.Amp) a.Amp = groupsByName.Amp[0];
    }
    return a;
  }, [selections, mode, hasPlates, groupsByName]);

  // Plate models available for the currently selected Myrius model
  const myriusPlateModels = useMemo(() => {
    if (!isMyrius || !product?.variants?.length) return null;
    const s = new Set();
    for (const v of product.variants) {
      const pm = v.selections["Plate Model"];
      if (pm && (!v.selections.Model || v.selections.Model === selections.Model)) s.add(pm);
    }
    return s;
  }, [isMyrius, product, selections.Model]);

  // Colours available per Model (derived from accessory variants)
  const modelColours = useMemo(() => {
    if (!isMyrius || !product?.variants?.length || !groupsByName.Model) return null;
    const map = {};
    for (const v of product.variants) {
      const m = v.selections.Model;
      const c = v.selections.Colour;
      if (m && c) (map[m] = map[m] || new Set()).add(c);
    }
    return map;
  }, [isMyrius, product, groupsByName]);

  const variant = useMemo(() => {
    if (isMyrius) {
      if (!product?.variants?.length) return null;
      const acc = selections.Accessories
        ? product.variants.find(
            (v) =>
              v.selections.Accessories === selections.Accessories &&
              v.selections.Model === selections.Model &&
              v.selections.Colour === selections.Colour
          )
        : null;
      const plate =
        !selections.Accessories && selections["Plate Model"] && selections["Plate Size"]
          ? product.variants.find(
              (v) =>
                v.selections["Plate Model"] === selections["Plate Model"] &&
                v.selections["Plate Size"] === selections["Plate Size"]
            )
          : null;
      const chosen = acc || plate;
      if (!chosen) return null;
      const sels = acc
        ? { Model: selections.Model, Colour: selections.Colour, Accessories: selections.Accessories }
        : { "Plate Model": selections["Plate Model"], "Plate Size": selections["Plate Size"] };
      return {
        id: chosen.id,
        price: chosen.price,
        mrp: chosen.mrp || chosen.price,
        image: chosen.image || null,
        stock: chosen.stock == null ? 100 : chosen.stock,
        selections: sels,
      };
    }
    return findVariant(product, anchoredSelections);
  }, [product, anchoredSelections, isMyrius, selections]);

  if (!product) return null;

  const variantsPending =
    (product.variant_count ?? 0) > 0 && !(Array.isArray(product.variants) && product.variants.length);
  if (variantsPending) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="product-detail-loading">
        <div className="bg-white rounded-2xl px-8 py-6 flex items-center gap-3 shadow-2xl">
          <Loader2 size={20} className="animate-spin text-[#0056D2]" />
          <span className="text-sm font-bold text-gray-700">Loading options…</span>
        </div>
      </div>
    );
  }

  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
  const visibleSelections = (sels) => {
    if (!hasPlates) return sels || {};
    const keep = mode === "plate" ? ["Plate", "Color"] : mode === "switch" ? ["Module", "Amp", "Color"] : ["Module", "Color"];
    return Object.fromEntries(Object.entries(sels || {}).filter(([k]) => keep.includes(k)));
  };
  const displayPrice = variant?.price ?? product.price;
  const outOfStock = Number(product.stock) === 0 || (variant != null && Number(variant.stock) === 0);
  const displayMrp = variant?.mrp ?? product.mrp;
  const displayImage = variant?.image || product.image;
  const discount =
    displayMrp && displayMrp > displayPrice
      ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100)
      : 0;

  const select = (groupName, value) => {
    // Auto-save pending bulk quantities to cart before a non-bulk selection changes
    // (e.g. 1P + 6A qty 10, then user clicks 2P → the 1P/6A×10 line is pushed to cart).
    if (!BULK_INPUT_GROUPS.includes(groupName) && bulkLines.length) {
      for (const { v, qty: n } of bulkLines) {
        addItem(
          {
            ...product,
            price: v.price,
            mrp: v.mrp,
            image: v.image || product.image,
            id: cartLineId(v.id, visibleSelections(v.selections)),
            variantInfo: { variant_id: v.id, selections: visibleSelections(v.selections) },
          },
          n
        );
      }
      setBulkQty({});
    }
    setSelections((prev) => {
      const next = { ...prev, [groupName]: value };
      if (isMyrius) {
        if (groupName === "Model") {
          const allowedColours = new Set();
          for (const v of product.variants) {
            if (v.selections.Model === value && v.selections.Colour) allowedColours.add(v.selections.Colour);
          }
          if (allowedColours.size && !allowedColours.has(next.Colour)) {
            next.Colour = (groupsByName.Colour || []).find((c) => allowedColours.has(c));
          }
          if (next["Plate Model"]) {
            // snap plate model to one available for the new model
            const pms = new Set();
            for (const v of product.variants) {
              const pm = v.selections["Plate Model"];
              if (pm && (!v.selections.Model || v.selections.Model === value)) pms.add(pm);
            }
            if (!pms.has(next["Plate Model"])) {
              next["Plate Model"] = (groupsByName["Plate Model"] || []).find((x) => pms.has(x));
            }
          }
        }
        // Accessories and Plates are mutually exclusive — picking one deselects the other.
        if (groupName === "Accessories") {
          delete next["Plate Model"];
          delete next["Plate Size"];
        }
        if (groupName === "Plate Model") {
          delete next.Accessories;
          if (!next["Plate Size"]) next["Plate Size"] = groupsByName["Plate Size"]?.[0];
        }
        if (groupName === "Plate Size") {
          delete next.Accessories;
          if (!next["Plate Model"]) next["Plate Model"] = groupsByName["Plate Model"]?.[0];
        }
        return next;
      }
      if (!hasPlates && product?.variants?.length) {
        // Generic co-occurrence constraint: after picking a value, every other group
        // must hold a value that actually appears together with it in some variant
        // (e.g. Coil "FR 100m" only exists with 10/16 sq mm gauges — and vice versa).
        // Pool is restricted to the currently selected Type (when the product has a
        // Type group) so e.g. MCB curves never leak back into Isolator selections.
        let pool = product.variants.filter((v) => v.selections[groupName] === value);
        if (groupName !== "Type" && next.Type) {
          const typed = pool.filter((v) => v.selections.Type === next.Type);
          if (typed.length) pool = typed;
        }
        const co = {};
        for (const v of pool) {
          for (const [k, val] of Object.entries(v.selections)) {
            if (k === groupName) continue;
            (co[k] = co[k] || new Set()).add(val);
          }
        }
        for (const g of product.option_groups) {
          if (g.name === groupName) continue;
          if (!co[g.name]) {
            delete next[g.name]; // group doesn't apply (e.g. Curve for Isolator)
          } else if (!co[g.name].has(next[g.name])) {
            next[g.name] = g.values.find((v2) => co[g.name].has(v2));
          }
        }
      }
      if (hasPlates && groupName === "Amp" && groupsByName.Module && !AMP_COMBINED_MODULES.includes(next.Module)) {
        next.Module = SWITCH_MODULES[0];
      }
      if (hasPlates && groupName === "Module" && value === "Socket" && next.Amp && !SOCKET_AMPS.includes(next.Amp)) {
        next.Amp = "6A";
      }
      return next;
    });
    if (hasPlates) {
      if (groupName === "Plate") setMode("plate");
      else if (groupName === "Amp") setMode("switch");
      else if (groupName === "Module") setMode(AMP_COMBINED_MODULES.includes(value) ? "switch" : "module");
    }
  };

  // Cart line id includes the visible selections so the same variant id with a
  // different Model/Colour (e.g. Myrius NXT vs Myrius Regular) stays a separate line.
  const cartLineId = (vid, sels) =>
    `${product.id}::${vid}::${Object.values(sels || {}).join("-").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  const handleAdd = () => {
    const productForCart = {
      ...product,
      // Override price/image with selected variant if any
      price: displayPrice,
      mrp: displayMrp,
      image: displayImage,
      id: variant ? cartLineId(variant.id, visibleSelections(variant.selections)) : product.id,
    };
    productForCart.variantInfo = variant
      ? { variant_id: variant.id, selections: visibleSelections(variant.selections) }
      : null;
    addItem(productForCart, qty);
    setAdded(true);
    setTimeout(() => onClose?.(), 700);
  };

  // ---- Bulk qty ordering (Legrand modular configurators) ----
  const bulkAccVariant = (acc) => {
    const colour = selections.Colour || groupsByName.Colour?.[0];
    return product.variants.find(
      (v) =>
        v.selections.Accessories === acc &&
        (!v.selections.Model || v.selections.Model === selections.Model) &&
        (!v.selections.Colour || v.selections.Colour === colour)
    );
  };
  const bulkPlateVariant = (size) =>
    selections["Plate Model"] &&
    product.variants.find(
      (v) =>
        v.selections["Plate Model"] === selections["Plate Model"] &&
        v.selections["Plate Size"] === size &&
        (!v.selections.Model || v.selections.Model === selections.Model)
    );
  const bulkLines = (() => {
    if (!isMyrius && !isWireBulk && !isAmpBulk) return [];
    const lines = [];
    for (const [key, raw] of Object.entries(bulkQty)) {
      const n = parseInt(raw, 10);
      if (!n || n < 1) continue;
      const [kind, value] = [key.slice(0, key.indexOf("::")), key.slice(key.indexOf("::") + 2)];
      const v =
        kind === "acc"
          ? bulkAccVariant(value)
          : kind === "color"
          ? findVariant(product, { ...selections, Color: value })
          : kind === "amp"
          ? findVariant(product, { ...selections, Amps: value })
          : kind === "ways"
          ? findVariant(product, { ...selections, Ways: value })
          : kind === "flexy"
          ? findVariant(product, { ...selections, "Flexy Model": value })
          : kind === "enc"
          ? findVariant(product, { ...selections, "Enclosure Model": value })
          : kind === "wp"
          ? findVariant(product, { ...selections, "W/P Box Model": value })
          : kind === "acbox"
          ? findVariant(product, { ...selections, "AC Box Model": value })
          : bulkPlateVariant(value);
      if (v && v.stock !== 0) lines.push({ v, qty: n });
    }
    return lines;
  })();
  const bulkTotal = bulkLines.reduce((s, l) => s + l.v.price * l.qty, 0);
  const bulkCount = bulkLines.reduce((s, l) => s + l.qty, 0);

  const handleBulkAdd = () => {
    for (const { v, qty: n } of bulkLines) {
      addItem(
        {
          ...product,
          price: v.price,
          mrp: v.mrp,
          image: v.image || product.image,
          id: cartLineId(v.id, visibleSelections(v.selections)),
          variantInfo: { variant_id: v.id, selections: visibleSelections(v.selections) },
        },
        n
      );
    }
    setBulkQty({});
    setAdded(true);
    setTimeout(() => onClose?.(), 700);
  };

  const setBulkValue = (key, raw) => {
    const clean = raw.replace(/\D/g, "").slice(0, 4);
    setBulkQty((m) => ({ ...m, [key]: clean }));
  };

  const waProduct = {
    ...product,
    price: displayPrice,
    name: `${product.name}${
      variant
        ? ` (${Object.entries(visibleSelections(variant.selections))
            .map(([k, v]) => `${k}: ${v}`)
            .join(", ")})`
        : ""
    }`,
  };

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/60 z-50 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <div
        data-testid={DTID.modal}
        className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`relative bg-white w-full sm:max-w-3xl rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col transform transition-transform duration-300 ${
            open ? "translate-y-0" : "translate-y-8"
          }`}
        >
          <button
            data-testid={DTID.close}
            onClick={onClose}
            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-gray-100 grid place-items-center shadow"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          <div className="flex flex-col sm:flex-row overflow-hidden">
            {/* Image */}
            <div className="sm:w-[30%] bg-gray-50 relative shrink-0">
              <div className="h-44 sm:h-full sm:min-h-[320px]">
                <SmartImage
                  product={product}
                  src={displayImage}
                  width={800}
                  className="w-full h-full object-contain p-2"
                />
              </div>
              {product.qr_verified && (
                <div className="absolute top-4 left-4 bg-[#16A34A] text-white text-xs font-bold px-2 py-1 rounded flex items-center gap-1 shadow">
                  <ShieldCheck size={12} strokeWidth={3} /> QR Verified
                </div>
              )}
              {discount > 0 && (
                <div className="absolute top-4 right-14 bg-[#FF5722] text-white text-xs font-extrabold px-2.5 py-1 rounded">
                  {discount}% OFF
                </div>
              )}
            </div>

            {/* Body */}
            <div className="sm:w-[70%] overflow-y-auto p-5 sm:p-7">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#0056D2] font-bold">
                {product.brand}
              </div>
              <h2 className="mt-1 font-display text-xl sm:text-2xl font-black text-gray-900 leading-tight">
                {product.name}
              </h2>
              {product.description && (
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  {product.description}
                </p>
              )}

              {user ? (
                <div data-testid={DTID.price} className="mt-4 flex items-baseline gap-3">
                  <span className="text-2xl sm:text-3xl font-black text-gray-900">
                    ₹{displayPrice.toLocaleString("en-IN")}
                  </span>
                  {displayMrp > displayPrice && (
                    <span className="text-base text-gray-500 line-through">
                      ₹{displayMrp.toLocaleString("en-IN")}
                    </span>
                  )}
                  {discount > 0 && (
                    <span className="text-xs font-bold text-[#16A34A]">
                      Save ₹{(displayMrp - displayPrice).toLocaleString("en-IN")}
                    </span>
                  )}
                  <span className="text-[10px] font-bold text-gray-500">+ 18% GST</span>
                </div>
              ) : (
                <button
                  data-testid="modal-login-to-view-price"
                  onClick={() => { onClose?.(); openLogin(); }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0056D2]/10 text-[#0056D2] font-extrabold text-sm hover:bg-[#0056D2]/20"
                >
                  <Lock size={14} /> Login to view trade price
                </button>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                <span className="inline-flex items-center gap-1">
                  <Truck size={12} className="text-[#0056D2]" /> Fastest delivery
                </span>
                <span className="inline-flex items-center gap-1">
                  <RefreshCw size={12} className="text-[#0056D2]" />
                  Toll free home service
                </span>
              </div>

              {/* Pricing mode hint */}
              {hasVariants && hasPlates && (
                <div data-testid="pricing-mode-hint" className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFD700]/25 text-[11px] font-extrabold text-gray-800 uppercase tracking-wide">
                  {MODE_HINTS[mode]}
                </div>
              )}

              {/* Option groups */}
              {isMyrius && (
                <div className="mt-2 text-[11px] font-bold text-[#0056D2] bg-[#EFF6FF] rounded-md px-3 py-2" data-testid="myrius-price-hint">
                  Pick an Accessory (in your colour) OR a Plate (Model + Size) — or type Qty against multiple items and add them all at once
                </div>
              )}
              {hasVariants &&
                product.option_groups.map((group) => {
                  // Hide groups that don't apply to the selected Type (e.g. Curve for Isolator)
                  if (typeAllowed && group.name !== "Type" && !typeAllowed[group.name]) return null;
                  // Hide groups that don't apply to the selected sub-type (e.g. Ways for Flexy DB)
                  if (dbAllowed && group.name !== "Type" && !SUBTYPE_GROUPS.includes(group.name) && !dbAllowed[group.name]) return null;
                  if (isMyrius && group.name === "Plate Size" && !selections["Plate Model"]) return null;
                  const priceDriving =
                    !hasPlates ||
                    group.name === "Color" ||
                    (mode === "plate" && group.name === "Plate") ||
                    (mode === "switch" && (group.name === "Amp" || group.name === "Module")) ||
                    (mode === "module" && group.name === "Module");
                  return (
                  <div key={group.name} className="mt-5">
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-700">
                        {group.name}
                        {hasPlates && !priceDriving && (
                          <span className="ml-1.5 normal-case font-semibold text-gray-400">(not in price)</span>
                        )}
                      </div>
                      <div className="text-xs font-semibold text-gray-900">
                        {selections[group.name] || "Select"}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {group.values.map((value) => {
                        const isActive = selections[group.name] === value;
                        if (
                          isMyrius &&
                          group.name === "Plate Model" &&
                          myriusPlateModels &&
                          !myriusPlateModels.has(value)
                        )
                          return null;
                        const typeBlocked =
                          (!!typeAllowed &&
                            group.name !== "Type" &&
                            typeAllowed[group.name] &&
                            !typeAllowed[group.name].has(value)) ||
                          (!!dbAllowed &&
                            group.name !== "Type" &&
                            !SUBTYPE_GROUPS.includes(group.name) &&
                            dbAllowed[group.name] &&
                            !dbAllowed[group.name].has(value));
                        const myriusBlocked =
                          isMyrius &&
                          group.name === "Colour" &&
                          !!selections.Model &&
                          !!modelColours?.[selections.Model] &&
                          !modelColours[selections.Model].has(value);
                        const ampBlocked =
                          typeBlocked ||
                          myriusBlocked ||
                          (hasPlates &&
                            group.name === "Amp" &&
                            selections.Module === "Socket" &&
                            !SOCKET_AMPS.includes(value));
                        if (ampBlocked) return null; // hide non-applicable options — clean look
                        const bulkKey =
                          isMyrius && group.name === "Accessories"
                            ? `acc::${value}`
                            : isMyrius && group.name === "Plate Size"
                            ? `size::${value}`
                            : isWireBulk && group.name === "Color"
                            ? `color::${value}`
                            : isAmpBulk && group.name === "Amps"
                            ? `amp::${value}`
                            : isAmpBulk && group.name === "Ways"
                            ? `ways::${value}`
                            : isAmpBulk && group.name === "Flexy Model"
                            ? `flexy::${value}`
                            : isAmpBulk && group.name === "Enclosure Model"
                            ? `enc::${value}`
                            : isAmpBulk && group.name === "W/P Box Model"
                            ? `wp::${value}`
                            : isAmpBulk && group.name === "AC Box Model"
                            ? `acbox::${value}`
                            : null;
                        const chipBtn = (
                          <button
                            key={bulkKey ? undefined : value}
                            data-testid={DTID.optionBtn(group.name, value)}
                            disabled={ampBlocked}
                            onClick={() => !ampBlocked && select(group.name, value)}
                            title={
                              myriusBlocked
                                ? `Not available for ${selections.Model}`
                                : typeBlocked
                                ? `Not available for ${selections.Type}`
                                : ampBlocked
                                ? "Sockets available in 6A & 16A only"
                                : undefined
                            }
                            className={`px-3 py-1.5 ${bulkKey ? "rounded-l-md" : "rounded-md"} border text-xs font-bold uppercase tracking-wide transition-all ${
                              ampBlocked
                                ? "border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed"
                                : isActive
                                ? priceDriving
                                  ? "border-[#0056D2] bg-[#0056D2] text-white shadow-sm"
                                  : "border-gray-400 bg-gray-200 text-gray-700"
                                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                            }`}
                          >
                            {value}
                          </button>
                        );
                        if (!bulkKey) return chipBtn;
                        return (
                          <div key={value} className="inline-flex items-stretch">
                            {chipBtn}
                            <input
                              data-testid={`bulk-qty-${group.name === "Accessories" ? "acc" : group.name === "Color" ? "color" : group.name === "Amps" ? "amp" : group.name === "Ways" ? "ways" : group.name === "Flexy Model" ? "flexy" : group.name === "Enclosure Model" ? "enc" : group.name === "W/P Box Model" ? "wp" : group.name === "AC Box Model" ? "acbox" : "size"}-${value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              inputMode="numeric"
                              placeholder="Qty"
                              disabled={ampBlocked}
                              value={ampBlocked ? "" : bulkQty[bulkKey] || ""}
                              onChange={(e) => setBulkValue(bulkKey, e.target.value)}
                              className={`w-11 -ml-px rounded-r-md border text-center text-xs font-extrabold outline-none transition-colors ${
                                (parseInt(bulkQty[bulkKey], 10) || 0) > 0
                                  ? "border-[#FF5722] bg-[#FFF3EF] text-[#E64A19]"
                                  : "border-gray-300 bg-gray-50 text-gray-700 focus:border-[#0056D2]"
                              }`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}

              {hasVariants && !variant && (
                <div className="mt-3 text-xs text-[#FF5722] font-semibold">
                  This combination is unavailable — pick a different option.
                </div>
              )}

              {outOfStock && (
                <div data-testid="detail-out-of-stock" className="mt-3 flex flex-wrap items-center gap-2 text-xs font-bold text-[#FF5722]">
                  No stock — coming soon.
                  <a
                    href={buildStockEnquiryUrl(product)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#128C7E] underline font-extrabold"
                  >
                    <WhatsAppIcon size={13} /> WhatsApp us for information
                  </a>
                </div>
              )}

              {/* Qty + actions */}
              {user ? (
              <div className="mt-6 flex items-stretch gap-3">
                <div className="inline-flex items-center bg-gray-100 rounded-md">
                  <button
                    data-testid={DTID.qtyDec}
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="w-10 h-11 grid place-items-center hover:bg-gray-200 rounded-l-md"
                    aria-label="Decrease quantity"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-10 text-center text-base font-extrabold">{qty}</span>
                  <button
                    data-testid={DTID.qtyInc}
                    onClick={() => setQty((q) => q + 1)}
                    className="w-10 h-11 grid place-items-center hover:bg-gray-200 rounded-r-md"
                    aria-label="Increase quantity"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                <button
                  data-testid={DTID.addBtn}
                  disabled={bulkCount > 0 ? false : (hasVariants && !variant) || outOfStock}
                  onClick={bulkCount > 0 ? handleBulkAdd : handleAdd}
                  className={`flex-1 h-11 rounded-md font-extrabold uppercase tracking-wide text-sm transition-all inline-flex items-center justify-center gap-2 ${
                    added
                      ? "bg-[#16A34A] text-white"
                      : "bg-[#FF5722] hover:bg-[#E64A19] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  }`}
                >
                  {added ? (
                    <>
                      <Check size={16} /> Added
                    </>
                  ) : bulkCount > 0 ? (
                    `Add ${bulkCount} items · ₹${bulkTotal.toLocaleString("en-IN")}`
                  ) : outOfStock ? (
                    "No Stock · Coming Soon"
                  ) : (
                    `Add to Cart · ₹${(displayPrice * qty).toLocaleString("en-IN")}`
                  )}
                </button>
                <a
                  data-testid={DTID.whatsapp}
                  href={buildProductWhatsAppUrl(waProduct, qty)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Order on WhatsApp"
                  title="Order on WhatsApp"
                  className="shrink-0 w-11 h-11 rounded-md bg-[#25D366] hover:bg-[#1FB855] text-white grid place-items-center transition-colors"
                >
                  <WhatsAppIcon size={20} />
                </a>
              </div>
              ) : (
                <button
                  data-testid="modal-login-to-order"
                  onClick={() => { onClose?.(); openLogin(); }}
                  className="mt-6 w-full h-11 rounded-md bg-[#FF5722] hover:bg-[#E64A19] text-white font-extrabold uppercase tracking-wide text-sm inline-flex items-center justify-center gap-2"
                >
                  <Lock size={15} /> Login to order
                </button>
              )}

              {user && product.bulk_unlock_price && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#FFD700] text-gray-900 text-xs font-extrabold uppercase tracking-wider">
                  Bulk price unlocked at ₹{product.bulk_unlock_price.toLocaleString("en-IN")} / unit
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
